import { ReactNode, useMemo, useState } from "react";
import ChartCard from "./ChartCard";
import SegmentedToggle from "./SegmentedToggle";
import StackedColumns, { EmptyChartNote } from "./charts/StackedColumns";
import { NoteCard } from "./Layout";
import { Freshness } from "./Pills";
import { formatNumber } from "../format";
import { viz } from "../styles";
import { CountDay } from "../types";
import {
  GRANULARITIES,
  Granularity,
  bucketCounts,
  formatBucketLabel,
  formatBucketTick,
  isPartialBucket,
} from "../volume";

/**
 * A single count series over time, with the same day/week/month toggle the
 * volume charts use.
 *
 * Factored out rather than written per screen for the reason `SegmentedToggle`
 * already gives: there are now several of these — three on the Instagram
 * screen, one on the Events screen — and a chart that looks *almost* like the
 * volume chart reads as a different kind of thing when it isn't. It shares
 * `volume.ts`'s bucketing outright, so a "week" is the same Monday-start UTC
 * week on every chart in this tool.
 *
 * **Deliberately not windowed.** `VolumeChartCard` trims to a default window
 * per granularity because it draws 1,700 days of history. Every series this
 * card is used for is at most ~100 days — the Instagram feature launched in
 * May 2026, and the events window is 100 days by construction — so the whole
 * history fits on one axis at any granularity, and trimming could only lop off
 * the oldest bucket for no benefit.
 *
 * **`startsFrom` is the honesty prop and it is not decorative.** One of the
 * series this card draws (Instagram automation fires) has no reconstructable
 * history at all and begins the day the nightly snapshot started. Passing that
 * date renders a standing note under the chart, so a three-bar chart cannot be
 * mistaken for a three-bar measurement of a long-running thing.
 */
const CountChartCard = ({
  title,
  subtitle,
  seriesLabel,
  days,
  color = viz.series1,
  freshness,
  defaultGranularity = "week",
  emptyLabel,
  error,
  loading,
  startsFrom,
  footnote,
}: {
  title: string;
  subtitle: string;
  /** Names the bar — "Accounts connected", "Products created". */
  seriesLabel: string;
  days: CountDay[];
  color?: string;
  freshness: Freshness;
  defaultGranularity?: Granularity;
  emptyLabel: string;
  error?: string;
  loading?: boolean;
  /**
   * Rendered as a standing note when this series does NOT reach back as far as
   * the thing it measures — i.e. when the chart starts because tracking
   * started, not because the activity did.
   */
  startsFrom?: ReactNode;
  /** Any further caveat that belongs on the card rather than in a README. */
  footnote?: ReactNode;
}) => {
  const [granularity, setGranularity] = useState<Granularity>(defaultGranularity);

  const series = useMemo(
    () => [{ key: "value", label: seriesLabel, color }],
    [seriesLabel, color]
  );

  const buckets = useMemo(() => bucketCounts(days, granularity), [days, granularity]);

  const newest = buckets[buckets.length - 1];
  const partial = isPartialBucket(newest, granularity);
  const total = buckets.reduce((sum, bucket) => sum + bucket.value, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ChartCard
        title={title}
        subtitle={
          buckets.length > 0 ? `${subtitle} · ${formatNumber(total)} shown` : subtitle
        }
        series={series}
        freshness={freshness}
        action={
          <SegmentedToggle
            label="Granularity"
            options={GRANULARITIES}
            value={granularity}
            onChange={setGranularity}
          />
        }
        table={{
          categoryLabel:
            granularity === "day" ? "Day" : granularity === "week" ? "Week of" : "Month",
          rows: buckets.map(bucket => ({
            category: formatBucketTick(bucket.date, granularity),
            values: { value: formatNumber(bucket.value) },
          })),
        }}
      >
        {error ? (
          <EmptyChartNote label={error} />
        ) : loading ? (
          <EmptyChartNote label="Loading…" />
        ) : buckets.length === 0 ? (
          <EmptyChartNote label={emptyLabel} />
        ) : (
          <StackedColumns
            data={buckets}
            series={series}
            categoryKey="date"
            height={180}
            tickFormatter={value => formatBucketTick(value, granularity)}
            labelFormatter={value => formatBucketLabel(value, granularity)}
          />
        )}
      </ChartCard>

      {startsFrom && <NoteCard title="This chart does not go back further">{startsFrom}</NoteCard>}

      {footnote && !startsFrom && <NoteCard title="Worth knowing">{footnote}</NoteCard>}

      {partial && newest && buckets.length > 1 && (
        <NoteCard title={`The last ${granularity} is still in progress`}>
          {formatBucketLabel(newest.date, granularity)} covers {newest.dayCount} day
          {newest.dayCount === 1 ? "" : "s"} so far, not a full {granularity}. Its bar is
          a running total and will keep climbing — it is not a drop.
        </NoteCard>
      )}
    </div>
  );
};

export default CountChartCard;
